import { bool, number, object, string } from "prop-types";
import { useId } from "react";

import styled from "styled-components/macro";

import { iconSizeType } from "./components/cssLength";

UnoHeadset.propTypes = {
  color: string,
  size: iconSizeType,
  style: object,
};

/**
 * Uno wearing a support headset. Same shape and API as UnoLogo -- sized by
 * its container, and duotone: Uno in the given color, headset in a lighter
 * tone of it.
 */
export default function UnoHeadset({ color = "#278dc1", size, style }) {
  if (size || style)
    return (
      <UnoHeadsetWrapper style={{ width: size, height: size, ...style }}>
        <Inside style={{ height: size }}>
          <Svg color={color} />
        </Inside>
      </UnoHeadsetWrapper>
    );

  return <Svg color={color} />;
}

export const UnoHeadsetWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Inside = styled.div`
  aspect-ratio: 1 / 1;
`;

// Scale and angle were dialled in together: 1.15 puts the band on the crown
// with the cups still inside Uno's outline, and -18 matches the tilt of his
// head. Bigger than this and the mic falls off his belly.
const HEADSET_TRANSFORM = "translate(103.5 31.2) scale(1.15) rotate(-18 50 60)";

/**
 * Band, cups, boom and mic. Drawn twice: once whole and in black to cut the
 * hole, then again as `accentsOnly` to put the lighter tone back on the cups
 * and the mic. The band and boom stay holes -- toning those as well washed the
 * whole headset out against the wool.
 */
const Headset = ({ accentsOnly, color, opacity }) => (
  <g
    transform={HEADSET_TRANSFORM}
    fill={color}
    fillOpacity={opacity}
    stroke={color}
    strokeOpacity={opacity}
    strokeLinecap="round"
  >
    {/* Band and boom weights are the design weight divided back down by the
        1.15 scale -- the headset was grown without thickening its lines, so
        rounding these off undoes that. */}
    {!accentsOnly && (
      <path d="M18,58 A34,34 0 0 1 82,58" fill="none" strokeWidth="11.53" />
    )}
    <rect x="5" y="54.03" width="19.51" height="31.93" rx="8.87" />
    <rect x="75.49" y="54.03" width="19.51" height="31.93" rx="8.87" />
    {!accentsOnly && (
      <path d="M84,90 q0,40 -54,40" fill="none" strokeWidth="7.98" />
    )}
    <circle cx="30" cy="130" r="8.87" />
  </g>
);

Headset.propTypes = { accentsOnly: bool, color: string, opacity: number };

const Svg = ({ color }) => {
  // Two of these on a page would otherwise share one mask id and the loser
  // renders as a plain sheep.
  const maskId = `uno_headset_${useId()}`;

  return (
    <svg
      id="uno_headset"
      data-name="Uno Headset"
      xmlns="http://www.w3.org/2000/svg"
      // Square viewBox, not UnoLogo's 249x207: the icon is laid out in a
      // square box, and a wide viewBox leaves the drawing sitting at the top
      // of it. -21.18 is half the leftover height.
      viewBox="0 -21.18 249.36 249.36"
    >
      <title>uno headset</title>
      {/* The headset is a hole, not a shape drawn on top. Where it crosses
          Uno's face the two cutouts flow together; painted white instead it
          inverts over the face and reads as a smudge. */}
      <mask
        id={maskId}
        maskUnits="userSpaceOnUse"
        x="-400"
        y="-400"
        width="1100"
        height="1100"
      >
        <rect x="-400" y="-400" width="1100" height="1100" fill="#fff" />
        <Headset color="#000" />
      </mask>
      {/* translate matches UnoLogo -- it's what fits the drawing to the viewBox. */}
      <g transform="translate(0 -20.71)">
        <g mask={`url(#${maskId})`}>
          <path
            style={{ fill: color }}
            d="M249.36,109.07a29.86,29.86,0,0,0-21.41-28.63c.06-.77.12-1.54.12-2.32a29.86,29.86,0,0,0-29.86-29.86c-.65,0-1.28.06-1.92.1A29.85,29.85,0,0,0,150.9,32.8a35.76,35.76,0,0,0-50.7-2.92,29.81,29.81,0,0,0-45,12.38A40.38,40.38,0,0,0,26.09,72,40.32,40.32,0,0,0,3.4,125.89a40.33,40.33,0,0,0,23.6,51,40.57,40.57,0,0,0,5,5.9L24.5,198.22A10.81,10.81,0,1,0,44,207.64l7-14.43a40.21,40.21,0,0,0,8.66,1.06L52.3,213a10.81,10.81,0,1,0,20.14,7.88L81,198.89a33.74,33.74,0,0,0,3.88.24,34.12,34.12,0,0,0,17.77-5,33.94,33.94,0,0,0,25.94,1.33,29.72,29.72,0,0,0,37.59,1.79,41.52,41.52,0,0,0,7.39,1.54l8,20.33a10.81,10.81,0,1,0,20.14-7.88l-6.15-15.72a41.65,41.65,0,0,0,7.21-4.09L208.32,203a10.81,10.81,0,1,0,19.46-9.42L217.56,172.4c.2-.5.41-1,.58-1.51A29.8,29.8,0,0,0,237,133.2,29.78,29.78,0,0,0,249.36,109.07Zm-29-12.8a28.11,28.11,0,0,1-21,16.5c-4.91,1-5.85,3-4.72,7.64,2.75,11.41,4.47,22.95,2.51,34.77-1.64,9.91-6.49,16.41-13.66,18.35h0l0,0h0c-7.11,2.13-14.66-.82-21.29-8.37-7.91-9-12.54-19.71-16.23-30.85-1.51-4.57-3.37-5.76-8-4a28.11,28.11,0,0,1-26.56-2.92c-3.24-2.12-3.51-4.48-.57-7.06,5.66-5,11.55-9.54,19.36-10.61,4.42-.61,7.16-3,9.1-7.1a31.52,31.52,0,0,1,19.18-16.88h0l0,0h0A31.52,31.52,0,0,1,183.6,90c3.8,2.44,7.4,3.05,11.48,1.23,7.19-3.21,14.61-2.43,22-1.21C221,90.61,222,92.75,220.36,96.27Z"
          />
        </g>
        {/* Outside the masked group on purpose: that mask hides the region
            this paints into. Cups and mic only -- toning the band as well
            washed the whole headset out against the wool. 0.5 and not fa's
            0.6: painting into the hole means the backdrop is what's behind
            these, so half alpha lands them exactly between Uno and it. */}
        <Headset color={color} opacity={0.5} accentsOnly />
      </g>
    </svg>
  );
};

Svg.propTypes = { color: string };
